import React, { useState } from "react";
import { X, Flag, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useCrypto } from "../context/CryptoContext";

// target: { type: "post"|"comment"|"dm"|"profile", id, authorId, snippet }
export const DisturbanceModal = ({ target, onClose }) => {
  const { encryptForSafety } = useCrypto();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      // Encrypt the flagged content to the Safety Review key, only Security/Signal
      // stewards can decrypt it. No plaintext content ever leaves the device.
      let sr = null;
      try { sr = await encryptForSafety(target.snippet || ""); } catch { sr = null; }
      await api.post("/disturbances", {
        target_type: target.type,
        target_id: target.id,
        target_author_id: target.authorId || null,
        sr,
        reason: reason.trim(),
      });
      setDone(true);
    } catch {
      alert("Could not send. Please try again.");
    }
    setBusy(false);
  };

  const noun = target.type === "dm" ? "message" : target.type;

  return (
    <div className="fixed inset-0 z-[130] bg-black/85 flex items-center justify-center p-4" data-testid="disturbance-modal" onClick={onClose}>
      <div className="bg-[#121212] border border-white/10 max-w-md w-full p-6 fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-white flex items-center gap-2"><Flag size={16} className="text-[#A855F7]" /> Report a disturbance</h3>
          <button onClick={onClose} data-testid="disturbance-close" className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        {done ? (
          <div className="text-center py-6" data-testid="disturbance-done">
            <p className="font-body text-sm text-[#A855F7] mb-2">Sent to The Watch.</p>
            <p className="font-body text-xs text-zinc-500 mb-6">Thank you. The Council will review this. Your report stays anonymous to the member, and a confirmation is waiting in your notifications.</p>
            <button onClick={onClose} className="bg-[#A855F7] text-white font-medium px-6 py-2.5 hover:bg-[#9333EA] transition-colors font-body">Done</button>
          </div>
        ) : (
          <>
            <p className="font-body text-sm text-zinc-400 mb-4 leading-snug">
              Flagging this {noun} sends it to the Council's <span className="text-white">Watch</span> for review.
              {(target.type === "post" || target.type === "comment") ? " It will be hidden immediately, pending their decision." : ""} You stay anonymous to the member.
            </p>
            {target.snippet && (
              <div className="border border-white/10 bg-black/30 p-3 mb-4 max-h-28 overflow-y-auto">
                <p className="font-body text-[10px] uppercase tracking-wider text-zinc-600 mb-1">What you're flagging</p>
                <p className="font-body text-xs text-zinc-300 whitespace-pre-wrap break-words">{target.snippet}</p>
              </div>
            )}
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you flagging this? (optional)" data-testid="disturbance-reason" rows={3} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#A855F7] font-body resize-none mb-4" />
            <button onClick={submit} disabled={busy} data-testid="disturbance-submit" className="w-full bg-[#A855F7] text-white font-medium px-6 py-3 hover:bg-[#9333EA] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <><Flag size={15} /> Send to The Watch</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
